/**
 * usePlatformConfig Hook
 * Fetches and caches platform configuration from admin settings
 * Used across the app to check pricing, features, and limits
 * Includes real-time socket updates for instant config changes
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import api from '../services/api';

// Cache the config to avoid repeated fetches and rate limiting
let cachedConfig = null;
let lastFetchTime = 0;
const CACHE_DURATION = 30000; // 30 seconds - reduce API calls to avoid rate limiting

export const usePlatformConfig = () => {
  const [config, setConfig] = useState(cachedConfig);
  const [loading, setLoading] = useState(!cachedConfig);
  const [error, setError] = useState(null);
  const [maintenance, setMaintenance] = useState(false);
  const [backendReachable, setBackendReachable] = useState(!!cachedConfig);
  const backendUnreachableRef = useRef(false);

  const fetchConfig = useCallback(async (force = false) => {
    const cacheAge = Date.now() - lastFetchTime;
    if (!force && cachedConfig && cacheAge < CACHE_DURATION) {
      setConfig(cachedConfig);
      setLoading(false);
      return cachedConfig;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await api.getPlatformConfig(force);

      if (data.success && data.config) {
        backendUnreachableRef.current = false;
        setBackendReachable(true);
        cachedConfig = data.config;
        lastFetchTime = Date.now();
        setConfig(data.config);
        setMaintenance(data.config.maintenance?.enabled || false);
        setLoading(false);
        return data.config;
      } else {
        throw new Error(data.error || 'Failed to load config');
      }
    } catch (err) {
      backendUnreachableRef.current = true;
      setError(err.message);
      if (cachedConfig) {
        setConfig(cachedConfig);
        setLoading(false);
        return cachedConfig;
      }
      const defaults = getDefaultConfig();
      setConfig(defaults);
      setLoading(false);
      return defaults;
    }
  }, []);

  useEffect(() => {
    fetchConfig();

    const interval = setInterval(() => {
      const cacheAge = Date.now() - lastFetchTime;
      const intervalMs = backendUnreachableRef.current ? 60000 : 30000;
      if (cacheAge >= intervalMs) fetchConfig(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchConfig]);

  const socketRef = useRef(null);

  useEffect(() => {
    if (!backendReachable) return;

    const getSocketUrl = () => {
      if (process.env.REACT_APP_RAILWAY_URL) return process.env.REACT_APP_RAILWAY_URL;
      if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3001';
      return 'https://api.yobitch.fun';
    };

    socketRef.current = io(getSocketUrl(), {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 2,
      reconnectionDelay: 5000,
    });

    socketRef.current.on('platform-config-update', update => {
      setConfig(prev => {
        if (!prev) return prev;
        const newConfig = {
          ...prev,
          features: update.features || prev.features,
          maintenance: update.maintenance || prev.maintenance,
          killSwitches: update.killSwitches || prev.killSwitches,
          limits: update.limits || prev.limits,
          pricing: update.pricing || prev.pricing,
          toolFees: update.toolFees || prev.toolFees,
          feeWallet: update.feeWallet ?? prev.feeWallet,
        };
        cachedConfig = newConfig;
        lastFetchTime = Date.now();
        return newConfig;
      });
      if (update.maintenance) {
        setMaintenance(update.maintenance.enabled || false);
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.off('platform-config-update');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [backendReachable]);

  /**
   * Get pricing for a specific tier
   */
  const getTierPrice = useCallback(
    tier => {
      return config?.pricing?.[tier] ?? 0;
    },
    [config]
  );

  /**
   * Get tool fee
   */
  const getToolFee = useCallback(
    tool => {
      return config?.toolFees?.[tool] ?? 0;
    },
    [config]
  );

  /**
   * Check if a tier is enabled
   */
  const isTierEnabled = useCallback(
    tier => {
      const key = `${tier}Enabled`;
      return config?.features?.[key] !== false;
    },
    [config]
  );

  /**
   * Check if a feature/tool is enabled
   */
  const isFeatureEnabled = useCallback(
    feature => {
      return config?.features?.[feature] !== false;
    },
    [config]
  );

  /**
   * Get limits for a tier
   */
  const getTierLimits = useCallback(
    tier => {
      const limits = config?.limits || {};

      switch (tier) {
        case 'basic':
          return { bundleWallets: 0, autoSellTriggers: 0 };
        case 'pro':
          return {
            bundleWallets: limits.proBundleWallets || 10,
            autoSellTriggers: limits.proAutoSellTriggers || 3,
          };
        case 'max':
          return {
            bundleWallets: limits.maxBundleWallets || 20,
            autoSellTriggers: limits.maxAutoSellTriggers || 5,
          };
        case 'ultra':
          return {
            bundleWallets: limits.ultraBundleWallets || 999,
            autoSellTriggers: limits.ultraAutoSellTriggers || 999,
          };
        default:
          return { bundleWallets: 0, autoSellTriggers: 0 };
      }
    },
    [config]
  );

  /**
   * Get fee wallet address
   */
  const getFeeWallet = useCallback(() => {
    return config?.feeWallet || null;
  }, [config]);

  /**
   * Calculate total launch cost
   */
  const calculateLaunchCost = useCallback(
    (tier, options = {}) => {
      let total = getTierPrice(tier);

      if (options.volumeBot && isFeatureEnabled('volumeBotEnabled')) {
        total += getToolFee('volumeBot');
      }
      if (options.walletMixer && isFeatureEnabled('walletMixerEnabled')) {
        total += getToolFee('walletMixer');
      }
      if (options.vanityAddress && isFeatureEnabled('vanityEnabled')) {
        total += getToolFee('vanityAddress');
      }
      if (options.airdrop && isFeatureEnabled('airdropEnabled')) {
        total += getToolFee('airdrop');
      }
      if (options.dexScreener && isFeatureEnabled('dexScreenerEnabled')) {
        total += getToolFee('dexScreener');
      }

      return total;
    },
    [getTierPrice, getToolFee, isFeatureEnabled]
  );

  /**
   * Get available tiers (only enabled ones)
   */
  const getAvailableTiers = useCallback(() => {
    const tiers = [];
    if (isTierEnabled('basic')) tiers.push('basic');
    if (isTierEnabled('pro')) tiers.push('pro');
    if (isTierEnabled('max')) tiers.push('max');
    if (isTierEnabled('ultra')) tiers.push('ultra');
    return tiers;
  }, [isTierEnabled]);

  return {
    config,
    loading,
    error,
    maintenance,
    refresh: () => fetchConfig(true),

    // Pricing
    getTierPrice,
    getToolFee,
    calculateLaunchCost,
    getFeeWallet,

    // Features
    isTierEnabled,
    isFeatureEnabled,
    getAvailableTiers,

    // Limits
    getTierLimits,
  };
};

/**
 * Default config if API fails
 */
const getDefaultConfig = () => ({
  pricing: { basic: 0, pro: 0.05, max: 0.15, ultra: 0.3 },
  // Note: dexScreener fee is platform fee only (~0.1 SOL). User pays DexScreener directly (~$299 USD)
  toolFees: {
    volumeBot: 0.02,
    walletMixer: 0.03,
    vanityAddress: 0.01,
    airdrop: 0.01,
    dexScreener: 0.1,
  },
  features: {
    basicEnabled: true,
    proEnabled: true,
    maxEnabled: true,
    ultraEnabled: true,
    volumeBotEnabled: true,
    walletMixerEnabled: true,
    autoSellEnabled: true,
    jitoEnabled: true,
    vanityEnabled: true,
    airdropEnabled: true,
    dexScreenerEnabled: true,
  },
  limits: {
    proBundleWallets: 10,
    maxBundleWallets: 20,
    proAutoSellTriggers: 3,
    maxAutoSellTriggers: 5,
  },
  maintenance: { enabled: false, message: '' },
  feeWallet: '',
});

export default usePlatformConfig;
